import { useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";

import { SectionHeading } from "@/components/section-heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { listNoticeDeliveries, retryNoticeDelivery } from "@/lib/admin.functions";
import { listResendableNotices, resendFailedNotices } from "@/lib/email-admin.functions";


export const Route = createFileRoute("/_authenticated/admin/email-delivery")({
  head: () => ({
    meta: [
      { title: "Email delivery — Quibblies staff" },
      {
        name: "description",
        content:
          "Delivery status, queue times and retries for every account decision and appeal notice sent by Quibblies.",
      },
      { property: "og:title", content: "Email delivery — Quibblies staff" },
      {
        property: "og:description",
        content: "Track failures, queue times and successful sends for decision notices.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: EmailDeliveryPage,
});

function statusVariant(status: string) {
  if (status === "sent") return "default" as const;
  if (status === "failed" || status === "bounced" || status === "complained")
    return "destructive" as const;
  return "outline" as const;
}

/** Plain-English reasons behind a failed or blocked delivery. */
const REASON_LABELS: Record<string, string> = {
  rate_limited: "Sending allowance reached — waiting before the next attempt",
  recipient_suppressed: "Recipient unsubscribed or previously bounced",
  no_recipient: "No email address on file",
  send_failed: "The email service refused the send",
  bounced: "The address bounced",
  complained: "Marked as spam by the recipient",
  domain_not_verified: "Sending domain is not verified yet",
  emails_disabled: "Email sending is switched off for this project",
};

function EmailDeliveryPage() {
  const queryClient = useQueryClient();
  const load = useServerFn(listNoticeDeliveries);
  const retry = useServerFn(retryNoticeDelivery);
  const resendFailed = useServerFn(resendFailedNotices);
  const listResendable = useServerFn(listResendableNotices);

  const { data, isLoading, error } = useQuery({
    queryKey: ["notice-deliveries"],
    queryFn: () => load(),
    retry: false,
  });

  // The resend runs in small batches so progress is real and stopping is
  // immediate: the current batch finishes, nothing further is started.
  const cancelRef = useRef(false);
  const [progress, setProgress] = useState<{
    running: boolean;
    done: number;
    total: number;
    sent: number;
    failed: number;
    suppressed: number;
    cancelled: boolean;
  } | null>(null);

  const retryMutation = useMutation({
    mutationFn: (noticeId: string) => retry({ data: { noticeId } }),
    onSuccess: (result) => {
      if (result.status === "sent") toast.success("Delivered");
      else if (result.status === "suppressed")
        toast.warning("Recipient is unsubscribed or previously bounced");
      else toast.error("Still failing", { description: result.error });
      void queryClient.invalidateQueries({ queryKey: ["notice-deliveries"] });
    },
    onError: (e: Error) => toast.error("Retry failed", { description: e.message }),
  });

  async function runResend() {
    cancelRef.current = false;
    let eligible: Array<{ id: string }> = [];
    try {
      eligible = await listResendable();
    } catch (e) {
      toast.error("Could not build the resend list", {
        description: e instanceof Error ? e.message : undefined,
      });
      return;
    }

    if (eligible.length === 0) {
      toast.info("Nothing eligible to resend", {
        description: "Failed notices without an address on file are skipped.",
      });
      return;
    }

    const BATCH = 5;
    let sent = 0;
    let failed = 0;
    let suppressed = 0;
    let done = 0;
    setProgress({
      running: true,
      done: 0,
      total: eligible.length,
      sent: 0,
      failed: 0,
      suppressed: 0,
      cancelled: false,
    });

    for (let index = 0; index < eligible.length; index += BATCH) {
      if (cancelRef.current) break;
      const slice = eligible.slice(index, index + BATCH);
      try {
        const result = await resendFailed({
          data: { noticeIds: slice.map((row) => row.id), limit: slice.length },
        });
        sent += result.sent;
        failed += result.failed;
        suppressed += result.suppressed;
      } catch (e) {
        failed += slice.length;
        toast.error("A batch failed", {
          description: e instanceof Error ? e.message : undefined,
        });
      }
      done += slice.length;
      setProgress({
        running: true,
        done,
        total: eligible.length,
        sent,
        failed,
        suppressed,
        cancelled: false,
      });
      void queryClient.invalidateQueries({ queryKey: ["notice-deliveries"] });
    }

    const cancelled = cancelRef.current;
    setProgress({
      running: false,
      done,
      total: eligible.length,
      sent,
      failed,
      suppressed,
      cancelled,
    });
    void queryClient.invalidateQueries({ queryKey: ["notice-deliveries"] });

    if (cancelled) {
      toast.warning(`Stopped after ${done} of ${eligible.length}`, {
        description: `${sent} delivered so far. The rest were left untouched and can be resent later.`,
      });
    } else {
      toast.success(`Resent ${sent} of ${eligible.length}`, {
        description: `${failed} still failing, ${suppressed} suppressed. Every attempt is in the audit log.`,
      });
    }
  }

  const counts = data?.counts ?? {};
  const delivered = (data?.rows ?? []).filter((r) => r.queue_seconds !== null);
  const averageQueue = delivered.length
    ? Math.round(
        delivered.reduce((sum, r) => sum + (r.queue_seconds ?? 0), 0) / delivered.length,
      )
    : null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Email delivery"
        description="Every account decision, appeal and support notice, with how its email went out. Retry anything that failed."
      />

      {error ? (
        <p className="mt-6 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm">
          {error.message}
        </p>
      ) : isLoading ? (
        <Skeleton className="mt-8 h-64 w-full rounded-2xl" />
      ) : (
        <>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {["sent", "queued", "failed", "suppressed", "skipped"].map((key) => (
              <div key={key} className="rounded-2xl border border-border/60 bg-surface/50 p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{key}</p>
                <p className="mt-1 font-display text-2xl text-foreground">{counts[key] ?? 0}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {averageQueue === null
                ? "No successful sends recorded yet."
                : `Average queue time before a notice was accepted for delivery: ${averageQueue}s.`}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="soft"
                onClick={() => void runResend()}
                disabled={progress?.running || (counts["failed"] ?? 0) === 0}
              >
                {progress?.running ? "Resending…" : "Resend all failed"}
              </Button>
              {progress?.running ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    cancelRef.current = true;
                    toast.info("Stopping after the current batch…");
                  }}
                >
                  Stop
                </Button>
              ) : null}
            </div>
          </div>

          {progress ? (
            <div
              className="mt-3 rounded-xl border border-border/60 bg-surface/40 p-4"
              aria-live="polite"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="text-foreground">
                  {progress.running
                    ? `Resending ${progress.done} of ${progress.total}…`
                    : progress.cancelled
                      ? `Stopped at ${progress.done} of ${progress.total}`
                      : `Finished ${progress.done} of ${progress.total}`}
                </span>
                <span className="text-xs text-muted-foreground">
                  {progress.sent} delivered · {progress.failed} still failing ·{" "}
                  {progress.suppressed} suppressed
                </span>
              </div>
              <Progress
                className="mt-2"
                value={progress.total ? (progress.done / progress.total) * 100 : 0}
              />
            </div>
          ) : null}

          <p className="mt-1 text-xs text-muted-foreground">
            Resending works through failed notices in small batches, so you can stop part-way
            without restarting; bounced, complained and suppressed recipients are skipped, and
            every attempt is written to the audit log.
          </p>



          <div className="mt-8 overflow-x-auto rounded-2xl border border-border/60">
            <table className="w-full min-w-[960px] text-left text-sm">
              <thead className="bg-surface/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Notice</th>
                  <th className="px-4 py-3">Recipient</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Queue</th>
                  <th className="px-4 py-3">Attempts</th>
                  <th className="px-4 py-3">Next retry</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows ?? []).map((row) => {
                  const attempts = row.attempts ?? 0;
                  const maxAttempts = row.max_attempts ?? 5;
                  const exhausted = attempts >= maxAttempts && row.delivery_status === "failed";
                  return (
                    <tr key={row.id} className="border-t border-border/50 align-top">
                      <td className="px-4 py-3">
                        <p className="text-foreground">{row.subject}</p>
                        <p className="text-xs text-muted-foreground">{row.template}</p>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{row.email || "—"}</td>
                      <td className="px-4 py-3">
                        <Badge variant={statusVariant(row.delivery_status)}>
                          {row.delivery_status}
                        </Badge>
                        {row.failure_reason ? (
                          <p className="mt-1 text-xs font-medium text-foreground">
                            {REASON_LABELS[row.failure_reason.split(":")[0] ?? ""] ??
                              row.failure_reason}
                          </p>
                        ) : null}
                        {row.delivery_error ? (
                          <p className="mt-1 max-w-[18rem] text-xs text-muted-foreground">
                            {row.delivery_error}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {row.queue_seconds === null ? "—" : `${row.queue_seconds}s`}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {attempts} / {maxAttempts}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {row.next_retry_at
                          ? new Date(row.next_retry_at).toLocaleString()
                          : exhausted
                            ? "Retries exhausted"
                            : "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(row.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        {row.delivery_status === "sent" ? null : (
                          <Button
                            size="sm"
                            variant="soft"
                            disabled={retryMutation.isPending}
                            onClick={() => retryMutation.mutate(row.id)}
                          >
                            Retry now
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Failed notices retry automatically with a widening gap (1, 5, 25 minutes, then
            longer) until {5} attempts are spent. Bounces and complaints are never retried.
          </p>

        </>
      )}
    </div>
  );
}
